/**
 * GSTFetchTiming — SUPER-ADMIN ONLY page showing the detailed per-notice
 * timing for one GST notice-fetch run. Opened from the "View Timing" button
 * in the fetch-results popup (route /gst/fetch-timing/:logId).
 *
 * Backed by GET /api/v1/gst/fetch-timing/{logId} which reads the run's
 * persisted timing JSON. Normal users never reach here (the button is
 * super-admin-gated and the backend 403s non-super-admins).
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Pagination,
  Skeleton,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { fetchData } from "../../api/apiService";
import { LAMBDA } from "../../api/endpointMap";
import { useAuth } from "../../context/AuthContext";

const { Title, Text } = Typography;

const METHOD_LABELS = {
  requests: "API (direct, fast)",
  api: "API (direct, fast)",
  selenium: "Selenium (browser)",
  "selenium-fallback": "Selenium (fallback)",
  lambda: "Cloud function (Lambda)",
  "lambda-api": "API Lambda",
  "lambda-selenium": "Selenium Lambda",
  "lambda-it": "Income Tax Lambda",
};

// This page is shared across all three portals (the timing endpoint is keyed by
// log id, not portal). Title + Type labels adapt to the log's portal_type.
const portalTitle = (pt) => {
  const k = (pt || "").trim().toLowerCase();
  if (k === "gst") return "GST";
  if (["income tax", "income_tax", "incometax"].includes(k)) return "Income Tax";
  if (["tds", "tds_tcs", "tds-tcs"].includes(k)) return "TDS";
  return "Notice"; // generic fallback
};

const KIND_LABELS = {
  // GST
  notice: "Notice & Order",
  gstr3a: "GSTR-3A",
  additional: "Additional Notice",
  // Income Tax
  demand: "Demand",
  e_proceeding: "E-Proceeding",
};

export default function GSTFetchTiming() {
  const { logId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Client-side pagination for the per-client cards list. A single fetch
  // can include hundreds of clients (229+ seen in production); rendering
  // every card + its inner Table at once was freezing the page on slower
  // machines. Slice locally — same antd Pagination control used across
  // the app, no new UI structure introduced.
  const PAGE_SIZE = 20;
  const [currentPage, setCurrentPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await fetchData(
        `${LAMBDA.GET_FETCH_TIMING_GST}/${logId}`,
      );
      setData(raw?.result || raw?.data?.result || null);
    } catch (e) {
      setError(e?.detail || e?.message || "Failed to load timing");
    } finally {
      setLoading(false);
    }
  }, [logId]);

  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
  }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" showIcon message="Super-admin only" />
      </div>
    );
  }

  const columns = [
    { title: "Reference ID", dataIndex: "ref_id", key: "ref_id" },
    {
      title: "Type",
      dataIndex: "kind",
      key: "kind",
      render: (k) => <Tag>{KIND_LABELS[k] || k}</Tag>,
    },
    { title: "Files", dataIndex: "files", key: "files", align: "right" },
    {
      title: "Elapsed (s)",
      dataIndex: "seconds",
      key: "seconds",
      align: "right",
      sorter: (a, b) => (a.seconds || 0) - (b.seconds || 0),
      render: (s) => (s == null ? "-" : s.toFixed(2)),
    },
  ];

  const clients = data?.clients || [];
  const failedClients = data?.failed_clients || [];
  // Slice the clients list for the current page. The total count drives
  // the Pagination control below; visibleClients is what we actually render.
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const visibleClients = clients.slice(startIdx, startIdx + PAGE_SIZE);

  // Columns for the Failed Clients table. Mirrors the admin
  // /admin/login-failed-logs surface (Reason ellipsis + tooltip + copy)
  // so operators see the same shape on both pages.
  const failedColumns = [
    {
      title: "Client",
      dataIndex: "client_name",
      key: "client_name",
      render: (v, row) => v || row.client_id || "-",
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (v) =>
        v ? (
          <Text
            style={{ maxWidth: 480 }}
            ellipsis={{ tooltip: v }}
            copyable={{ text: v }}
          >
            {v}
          </Text>
        ) : (
          "-"
        ),
    },
    {
      title: "Fetch type",
      dataIndex: "fetch_type",
      key: "fetch_type",
      width: 120,
      render: (v) => <Tag>{v || "Manual"}</Tag>,
    },
    {
      title: "Failed at",
      dataIndex: "failed_at",
      key: "failed_at",
      width: 180,
      render: (v) => (v ? new Date(v).toLocaleString() : "-"),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {portalTitle(data?.portal_type)} Fetch Timing
        </Title>
      </div>

      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
      )}

      {loading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 48, flexWrap: "wrap" }}>
              <Statistic
                title="Method"
                value={METHOD_LABELS[data?.fetch_method] || data?.fetch_method || "-"}
              />
              <Statistic title="Clients" value={data?.total_clients ?? clients.length} />
              <Statistic
                title="Total cost"
                value={
                  data?.total_cost_inr != null
                    ? `₹${Number(data.total_cost_inr).toFixed(4)}`
                    : "-"
                }
                valueStyle={{ color: "#7c3aed" }}
              />
            </div>
            <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
              Per-notice times overlap because notices are fetched in parallel;
              the client total below is the real wall-clock time. "fetch" is the
              Lambda fetch time; "save" is the time to download and store each
              file and insert the rows on our side.
            </Text>
          </Card>

          {clients.length === 0 && failedClients.length === 0 ? (
            <Alert type="info" showIcon message="No timing detail recorded for this run." />
          ) : (
            visibleClients.map((c, idx) => (
              <Card
                key={c.client_id || c.client_name || startIdx + idx}
                style={{ marginBottom: 16 }}
                type="inner"
                title={
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <Tag color="blue">{`Client ${startIdx + idx + 1} of ${clients.length}`}</Tag>
                    <Text strong style={{ fontSize: 15 }}>
                      {c.client_display_name || c.client_name}
                    </Text>
                    <Tag color={c.success ? "green" : "red"}>
                      {c.success ? "Success" : "Failed"}
                    </Tag>
                  </span>
                }
                extra={
                  <Text strong>
                    {/* Fetch = Lambda fetch wall-clock (total_seconds). Save =
                        backend download+store+insert wall-clock (save_seconds).
                        Show both, plus a combined total when both are present. */}
                    {c.total_seconds != null ? `${c.total_seconds.toFixed
                      ? c.total_seconds.toFixed(2)
                      : c.total_seconds}s fetch` : "-"}
                    {c.save_seconds != null ? (
                      <span style={{ color: "#b45309" }}>
                        {` · ${Number(c.save_seconds).toFixed(2)}s save`}
                      </span>
                    ) : ""}
                    {c.total_seconds != null && c.save_seconds != null ? (
                      <span>
                        {` · ${(Number(c.total_seconds) + Number(c.save_seconds)).toFixed(2)}s total`}
                      </span>
                    ) : ""}
                    {c.files_uploaded != null ? ` · ${c.files_uploaded} files` : ""}
                    {c.cost_inr != null && c.cost_inr > 0 ? (
                      <span style={{ color: "#7c3aed" }}>
                        {` · ₹${Number(c.cost_inr).toFixed(4)}`}
                      </span>
                    ) : ""}
                  </Text>
                }
              >
                {!c.success && c.error && (
                  <Alert
                    type="warning"
                    showIcon
                    message="This client's fetch failed"
                    description={c.error}
                    style={{ marginBottom: 12 }}
                  />
                )}
                <Table
                  size="small"
                  rowKey={(r, i) => `${r.ref_id}-${i}`}
                  columns={columns}
                  dataSource={c.per_notice || []}
                  pagination={false}
                />
              </Card>
            ))
          )}

          {clients.length > PAGE_SIZE && (
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <Pagination
                current={currentPage}
                total={clients.length}
                pageSize={PAGE_SIZE}
                onChange={(p) => setCurrentPage(p)}
                showSizeChanger={false}
                showQuickJumper
              />
            </div>
          )}

          {failedClients.length > 0 && (
            <Card
              style={{ marginBottom: 16 }}
              type="inner"
              title={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Tag color="red">Failed Clients</Tag>
                  <Text strong>{failedClients.length}</Text>
                </span>
              }
            >
              <Table
                size="small"
                rowKey="id"
                columns={failedColumns}
                dataSource={failedClients}
                pagination={false}
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
}
